import React from "react";
import "./Seedkeeper.css"; // Import the CSS file

function Seedkeeper() {
  return (
    <div className="seedkeeper-container">
      <h1>SeedKeeper Instructions</h1>
      <p>
        Learn how to set up and use the SeedKeeper tool. Follow the detailed
        steps below to build your own SeedKeeper or use the prebuilt
        applications for secure seed management.
      </p>
      <h2>Creating the SeedKeeper Card</h2>
      <ol>
        <li>
          Clone the{" "}
          <a
            href="https://github.com/Toporin/Seedkeeper-Applet"
            target="_blank"
            rel="noopener noreferrer"
            className="seedkeeper-link"
          >
            Seedkeeper Applet repository
          </a>{" "}
          repository:
          <div className="code-block">
            <code>git clone git@github.com:Toporin/Seedkeeper-Applet.git</code>
          </div>
        </li>
        <li>
          Build the Seedkeeper applet:
          <ul>
            <li>
              Option 1: Download an existing release from the{" "}
              <a
                href="https://github.com/Toporin/Seedkeeper-Applet/releases"
                target="_blank"
                rel="noopener noreferrer"
                className="satochip-link"
              >
                Seedkeeper Applet Releases
              </a>
              .
            </li>
            <li>
              Option 2: Follow the steps in the README to create your own build.
              <ul>
                <li>
                  Download the latest release of{" "}
                  <a
                    href="https://github.com/martinpaljak/ant-javacard/releases/latest/download/ant-javacard.jar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="satochip-link"
                  >
                    Ant-Javacard
                  </a>{" "}
                  and place it in the `lib` folder.
                </li>
                <li>
                  Install Apache Ant:
                  <div className="code-block">
                    <code>sudo apt install ant</code>
                  </div>
                </li>
                <li>
                  Use or install the recommended Java JDK (8 or 11):
                  <div className="code-block">
                    <code>sudo update-alternatives --config java</code>
                  </div>
                </li>
                <li>
                  Download the Javacard SDK into the `seedkeeper` project in the
                  `sdks` folder:
                  <div className="code-block">
                    <code>
                      git submodule add
                      https://github.com/martinpaljak/oracle_javacard_sdks sdks
                    </code>
                  </div>
                </li>
                <li>
                  Build the project using Apache Ant:
                  <div className="code-block">
                    <code>ant</code>
                  </div>
                  This will generate the `.cap` file in the `Seedkeeper-Applet`
                  folder.
                </li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          Put the <code>.cap</code> file in the same folder as the{" "}
          <code>gp.jar</code> file, which is located in the{" "}
          <code>tool/target</code> directory of the GlobalPlatformPro
          repository.
          <div className="code-block">
            <code>
              mv &lt;path-to-Applet&gt;/Seedkeeper-Applet/Seedkeeper-3.0.4.cap
              &lt;path-to-GP&gt;/GlobalPlatformPro/tool/target/
            </code>
          </div>
          This ensures the `.cap` file is in the correct directory for the
          loading process.
        </li>
        <li>
          Connect an NFC reader to your computer and insert your Java J3R180 NFC
          card.
        </li>
      </ol>

      <h2>Loading the Seedkeeper Applet on the Card</h2>
      <ul>
        <li>
          <strong>Windows:</strong>
          <ul>
            <li>
              List the applets on the card:
              <div className="code-block">
                <code>gp.exe -l</code>
              </div>
            </li>
            <li>
              Load the applet:
              <div className="code-block">
                <code>gp.exe -install .\SeedKeeper-3.0.4.cap</code>
              </div>
            </li>
            <li>
              Delete the applet:
              <div className="code-block">
                <code>gp.exe -uninstall .\SeedKeeper-3.0.4.cap</code>
              </div>
            </li>
          </ul>
        </li>
        <li>
          <strong>Ubuntu/Linux:</strong>
          <ul>
            <li>
              List the applets on the card:
              <div className="code-block">
                <code>java -jar gp.jar -l</code>
              </div>
            </li>
            <li>
              Load the applet:
              <div className="code-block">
                <code>java -jar gp.jar -install ./SeedKeeper-3.0.4.cap</code>
              </div>
            </li>
            <li>
              Delete the applet:
              <div className="code-block">
                <code>java -jar gp.jar -uninstall ./SeedKeeper-3.0.4.cap</code>
              </div>
            </li>
          </ul>
        </li>
      </ul>

      <h2>Supported Software Clients</h2>
      <p>SeedKeeper is compatible with the following software:</p>
      <ul>
        <li>
          Download the Seedkeeper applications:
          <ul>
            <li>
              For mobile:
              <ul>
                <li>
                  Android:{" "}
                  <a
                    href="https://github.com/Toporin/Seedkeeper-Android"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="seedkeeper-link"
                  >
                    Seedkeeper Android App
                  </a>
                </li>
                <li>
                  iOS:{" "}
                  <a
                    href="https://github.com/Toporin/Seedkeeper-iOS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="seedkeeper-link"
                  >
                    Seedkeeper iOS App
                  </a>
                </li>
              </ul>
            </li>
            <li>
              For desktop:{" "}
              <a
                href="https://github.com/Toporin/Seedkeeper-Tool/releases"
                target="_blank"
                rel="noopener noreferrer"
                className="seedkeeper-link"
              >
                Seedkeeper Tool for Windows & Linux
              </a>
            </li>
          </ul>
        </li>
        <li>
          Clone the{" "}
          <a
            href="https://github.com/Toporin/Seedkeeper-Tool"
            target="_blank"
            rel="noopener noreferrer"
            className="seedkeeper-link"
          >
            Seedkeeper-Tool repository
          </a>{" "}
          repository:
          <div className="code-block">
            <code>git clone git@github.com:Toporin/Seedkeeper-Tool.git</code>
          </div>
        </li>
        <li>
          Go into the repository.
          <div className="code-block">
            <code>cd Seedkeeper-Tool</code>
          </div>
        </li>
        <li>
          Optional: create and activate your virtual environment:
          <div className="code-block">
            <div>
              <code># Create the virtual environment</code>
            </div>
            <div>
              <code>python3 -m venv venv</code>
            </div>
            <div style={{ marginTop: "10px" }}>
              <code># Activate the virtual environment (Linux/Mac)</code>
            </div>
            <div>
              <code>source venv/bin/activate</code>
            </div>
            <div style={{ marginTop: "10px" }}>
              <code>
                # Activate the virtual environment (Windows Command Prompt)
              </code>
            </div>
            <div>
              <code>venv\Scripts\activate</code>
            </div>
          </div>
        </li>
        <li>
          Install the required Python dependencies:
          <div className="code-block">
            <code>
              python3 -m pip install -r
              Seedkeeper-Tool/contrib/requirements/requirements.txt
            </code>
          </div>
        </li>
        <li>
          Run the Seedkeeper-Tool software:
          <div className="code-block">
            <code>python3 seedkeeper/seedkeeper.py</code>
          </div>
        </li>
      </ul>

      <h2>Using the Tool</h2>
      <ul>
        <li>
          Download and install the appropriate Seedkeeper tool or app for your
          platform.
        </li>
        <li>
          Use the Seedkeeper-Tool or mobile apps to securely manage and store
          your recovery seeds, generate new secrets, and export/import them as
          needed.
          <ul>
            <li>
              <strong>Note:</strong> At this time, it is only possible to create
              up to 15 entries, and deletion is not supported. However, with the
              new software update, you will be able to create up to 100 entries
              and delete them.
            </li>
          </ul>
        </li>
        <li>
          Refer to the{" "}
          <a
            href="https://github.com/Toporin/Seedkeeper-Tool#how-to-use-your-seedkeeper"
            target="_blank"
            rel="noopener noreferrer"
            className="seedkeeper-link"
          >
            Seedkeeper-Tool Documentation
          </a>{" "}
          for detailed steps on how to use your SeedKeeper.
        </li>
        <li>
          You can also watch a tutorial on how the Seedkeeper iOS app works (see video links in the instructions above).
        </li>
        <li>
          SeedKeeper can be used in combination with your Satochip hardware
          wallet to manage and securely store recovery seeds. For detailed
          instructions, refer to the{" "}
          <a
            href="https://github.com/Toporin/Seedkeeper-Tool#how-to-use-seedkeeper-with-your-satochip"
            target="_blank"
            rel="noopener noreferrer"
            className="seedkeeper-link"
          >
            How to Use SeedKeeper with Your Satochip
          </a>{" "}
          section in the Seedkeeper-Tool documentation.
        </li>
      </ul>
      <h2>Troubleshooting</h2>
      <p>
        If you encounter issues with building or using the Seedkeeper applet,
        you can download a prebuilt `.cap` file directly from the link below:
      </p>
      <ul>
        <li>
          <a
            href={`${process.env.PUBLIC_URL}/assets/other/SeedKeeper-v0.2-0.1-beta2.cap`}
            download="SeedKeeper-3.0.4.cap"
            className="seedkeeper-link"
          >
            Download SeedKeeper CAP File
          </a>
        </li>
      </ul>
      <p>
        Ensure that the `.cap` file is placed in the correct directory and
        follow the instructions provided in the sections above to install it on
        your smart card.
      </p>
    </div>
  );
}

export default Seedkeeper;
